import { useDropzone } from 'react-dropzone';
import { mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import FormHelperText from '@mui/material/FormHelperText';

import { UploadProps } from './types';
import { uploadClasses } from './classes';
import { RejectionFiles } from './rejection-files';
import { MultiFilePreview } from './MultiFilePreview';
import { UploadPlaceholder } from './UploadPlaceholder';

// ----------------------------------------------------------------------

export function Upload({
  sx,
  value,
  error,
  disabled,
  onDelete,
  onUpload,
  onRemove,
  thumbnail,
  helperText,
  onRemoveAll,
  className,
  multiple = false,
  isWidget = false,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const isArray = Array.isArray(value) && multiple;

  const hasFile = !isArray && !!value;
  const hasFiles = isArray && !!value.length;

  const hasError = isDragReject || !!error;

  const renderMultiPreview = () =>
    hasFiles && (
      <MultiFilePreview files={value} thumbnail={thumbnail} onRemove={onRemove} sx={{ my: 3 }} />
    );

  return (
    <Box
      className={mergeClasses([uploadClasses.upload, className])}
      sx={[{ width: 1, position: 'relative' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Box
        {...getRootProps()}
        sx={[
          (theme) => ({
            outline: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            transition: theme.transitions.create(['opacity', 'padding']),
            '&:hover': { opacity: 0.72 },
            ...(isDragActive && { opacity: 0.72 }),
            ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
            ...(hasError && {
              color: 'error.main',
              borderColor: 'error.main',
              bgcolor: alpha(theme.palette.error.main, 0.08),
            }),
            ...(hasFile && { padding: '28% 0' }),
            ...(!isWidget && {
              p: 3,
              bgcolor: theme.palette.grey['500_8'] || alpha(theme.palette.grey[500], 0.08),
              border: `1px dashed ${theme.palette.grey['500_24'] || alpha(theme.palette.grey[500], 0.2)}`,
            }),
          }),
        ]}
      >
        <input {...getInputProps()} />

        <UploadPlaceholder isWidget={isWidget} />
      </Box>

      {helperText && (
        <FormHelperText error={!!error} sx={{ mx: 1.75 }}>
          {helperText}
        </FormHelperText>
      )}

      {!!fileRejections.length && <RejectionFiles files={fileRejections} />}

      {renderMultiPreview()}
    </Box>
  );
}
